import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Dropdown, Form } from "react-bootstrap";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import {
    Revision,
    RevisionData,
    RevisionsClientVariables,
    SortingMode
} from "../../models/types";
import { GET_REVISIONS_PER_USER } from "../../graphql/queries";
import { useLocalState } from "../../graphql/hooks";
import { SortableTitles } from "../simple/SortableTitles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDown,
    faArrowUp,
    faMagnifyingGlass
} from "@fortawesome/free-solid-svg-icons";
import { UpButton } from "../buttons/UpButton";
import { RevisionItem } from "../simple/RevisionItem";
import { sortItems, updateSearchTermInCache } from "../../common/Helpers";
import styles from "./ProductsPageClient.module.scss";

type SortField = "date" | "name";
type SortDirection = "asc" | "desc";

// Note: in SortingMode, DateAscending actually sorts newest-first (see sortItems
// in common/Helpers.ts), so "desc" (newest-first in the UI) maps to DateAscending.
const getSortMode = (
    field: SortField,
    direction: SortDirection
): SortingMode => {
    if (field === "date") {
        return direction === "desc"
            ? SortingMode.DateAscending
            : SortingMode.DateDescending;
    }
    return direction === "asc"
        ? SortingMode.NameAscending
        : SortingMode.NameDescending;
};

const ProductsPageClientImplementation = () => {
    const state = useLocalState();
    const [searchValue, setSearchValue] = useState<string>(
        state.searchTerm ? state.searchTerm : ""
    );
    const [sortedRevisions, updateSortedRevisions] = useState<Revision[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const client = useApolloClient();
    const [sortField, setSortField] = useState<SortField>("date");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const scrollableRef = useRef(null);
    const [
        fetchRevisions,
        {
            loading,
            // @ts-ignore
            data: { getRevisionsPerUser } = {},
            error
        }
    ] = useLazyQuery<RevisionData, RevisionsClientVariables>(
        GET_REVISIONS_PER_USER
    );

    const [
        fetchTotalCount,
        {
            // @ts-ignore
            data: { getRevisionsPerUser: allRevisions } = {}
        }
    ] = useLazyQuery<RevisionData, RevisionsClientVariables>(
        GET_REVISIONS_PER_USER
    );

    useEffect(() => {
        fetchRevisions();
        fetchTotalCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (allRevisions) {
            setTotalCount(allRevisions.length);
        }
    }, [allRevisions]);

    useEffect(() => {
        fetchRevisions({
            variables: {
                searchTerm: state.searchTerm
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.searchTerm]);

    useEffect(() => {
        if (!loading && getRevisionsPerUser) {
            const sortMode = getSortMode(sortField, sortDirection);
            const sorted: any[] = [...getRevisionsPerUser];
            sorted.sort((a, b) => sortItems(a, b, sortMode, false));
            updateSortedRevisions(sorted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        loading,
        sortField,
        sortDirection,
        getRevisionsPerUser,
        state.developerMode
    ]);

    const items = sortedRevisions?.map((revision: Revision) => (
        <RevisionItem
            key={revision.id}
            projectId={revision.project.id}
            revision={revision}
            developerMode={state.developerMode}
            showArchived={state.showArchived}
            showReleaseNotes={() => {}}
            onArchive={() => {}}
            onCreate={() => {}}
            onRemove={() => {}}
        />
    ));

    if (error) {
        return (
            <Container
                className={`${styles.widthProducts} ${
                    state.developerMode
                        ? "pageMaxWidthDev"
                        : "pageMaxWidthClient"
                } p-2 pt-5 d-flex justify-content-center text-contrast`}
            >
                <p>Error: {error.message}</p>
            </Container>
        );
    }

    return (
        <Container
            className={`pb-0 ${styles.widthProducts} ${
                state.developerMode ? "pageMaxWidthDev" : "pageMaxWidthClient"
            } p-3`}
        >
            <div className="sticky-header">
                <div className="p-2 mb-2 d-flex text-subtle align-items-center">
                    <div className={styles.listHeader}>
                        <span className={styles.listTitle}>Projects</span>
                        <span className={styles.listCount}>
                            {state.searchTerm
                                ? `(${sortedRevisions.length} of ${totalCount})`
                                : `(${totalCount})`}
                        </span>
                    </div>
                    <div className="ms-auto d-flex align-items-center gap-2">
                        <Form
                            onSubmit={event => {
                                event.preventDefault();
                            }}
                        >
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className={styles.searchIcon}
                                />
                                <Form.Control
                                    onChange={(event: any) => {
                                        setSearchValue(event.target.value);
                                        updateSearchTermInCache(
                                            client,
                                            event.target.value
                                        );
                                    }}
                                    type="text"
                                    id="searcFormInput"
                                    className={styles.searchInput}
                                    value={searchValue}
                                    placeholder="Filter..."
                                />
                            </div>
                        </Form>
                        <div className={styles.sortControls}>
                            <Button
                                variant="link"
                                className={styles.sortDirectionButton}
                                onClick={() =>
                                    setSortDirection(d =>
                                        d === "asc" ? "desc" : "asc"
                                    )
                                }
                            >
                                <FontAwesomeIcon
                                    icon={
                                        sortDirection === "asc"
                                            ? faArrowUp
                                            : faArrowDown
                                    }
                                    size="xs"
                                />
                            </Button>
                            <Dropdown className={styles.sortDropdown}>
                                <Dropdown.Toggle
                                    variant="link"
                                    className={styles.sortFieldButton}
                                    id="sort-field-dropdown"
                                >
                                    {sortField === "date"
                                        ? "Last updated"
                                        : "Name"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                        active={sortField === "date"}
                                        onClick={() => setSortField("date")}
                                    >
                                        Last updated
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        active={sortField === "name"}
                                        onClick={() => setSortField("name")}
                                    >
                                        Name
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                {state.developerMode && <SortableTitles />}
            </div>

            <div
                className="justify-content-center scrollable-container"
                ref={scrollableRef}
            >
                {items && items.length > 0 ? (
                    <div className={`p-1 ${styles.itemsList}`}>{items}</div>
                ) : (
                    <div>
                        <h6 className="text-muted mt-5 text-center">
                            <p>
                                {" "}
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className="me-2"
                                />
                                No items found.{" "}
                            </p>
                        </h6>
                    </div>
                )}
                {items && items.length > 15 && (
                    <Col className="d-flex justify-content-center mt-1">
                        <UpButton scrollableRef={scrollableRef} />
                    </Col>
                )}
            </div>
        </Container>
    );
};

export const ProductsPageClient = React.memo(ProductsPageClientImplementation);
